import React, { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';

import {
    useModal,
    noticeError,
    LightboxFullScreenTakeoverStyles,
} from '@ratehub/base-ui';

import SavingsInterestCalculationSummary from '../functions/SavingsInterestCalculationSummary';


/**
 * Creates a callback for viewing the Savings Interest Calculation modal.
 *
 * @return {(function({savingsAccount: SavingsAccountPropType, [p: string]: *}): null)|*}
 */
function useSavingsInterestCalculationCallback() {
    const modal = useModal();

    return useCallback(
        /**
         * To be invoked when a user has asked to see the Savings Interest Calculation modal.
         *
         * @param {SavingsAccountPropType} savingsAccount
         * @param {Object} otherProps
         * @returns {null}
         */
        ({
            savingsAccount,
            ...otherProps
        }) => {
            if (!savingsAccount) {
                noticeError(new RangeError('`savingsAccount` argument to callback must not be empty.'));

                return null;
            }

            // We use the modal’s layer id to close the modal programmatically
            modal.open({
                isContentFullWidth: true,
                showBranding: false,
                verticalAlignment: 'flex-start',

                style: LightboxFullScreenTakeoverStyles,    // semi-transparent

                screenReaderLabel: (
                    <FormattedMessage
                        id="web-components.savings-interest-calculation.screen-reader-title"
                        defaultMessage="Savings Interest Calculation"
                    />
                ),

                children: (
                    <SavingsInterestCalculationSummary
                        savingsAccount={savingsAccount}
                        {...otherProps}
                    />
                ),
            });
        },
        [ modal ],
    );
}


export default useSavingsInterestCalculationCallback;
